/**
 * <upsell-recommendations>
 *
 * Tier-3 (auto) fallback for the PDP upsell grid. Lazily fetches Shopify's
 * related products via the Section Rendering API, then fills the remaining
 * upsell slots client-side — deduping against products already shown by the
 * server-rendered tiers and respecting the block's exclusion tags + limit.
 *
 * Isolated from the theme's shared <product-recommendations> component (which
 * matches the first instance by tag) to avoid colliding with the PDP's
 * complementary-products block.
 */
class UpsellRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.loaded) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    observer.observe(this);
  }

  async loadRecommendations() {
    this.loaded = true;

    const grid = this.closest('.upsell-grid');
    const url = this.dataset.url;
    if (!grid || !url) {
      this.remove();
      return;
    }

    const limit = parseInt(this.dataset.limit, 10) || 0;
    const excludeTags = this.parseList(this.dataset.excludeTags);

    // Ids already shown (server tiers + anything live in the grid).
    const shown = new Set(this.parseList(this.dataset.shown));
    grid.querySelectorAll('.upsell-card[data-product-id]').forEach((card) => {
      shown.add(card.dataset.productId);
    });

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Upsell recommendations request failed: ${response.status}`);

      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const candidates = markup.querySelectorAll('.upsell-card[data-product-id]');

      let added = 0;
      candidates.forEach((card) => {
        if (added >= limit) return;

        const id = card.dataset.productId;
        if (!id || shown.has(id)) return;
        if (this.hasExcludedTag(card, excludeTags)) return;

        shown.add(id);
        added += 1;
        grid.insertBefore(document.importNode(card, true), this);
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.remove();
      // Auto-only grid that ended up empty: leave no stray spacing behind.
      if (!grid.querySelector('.upsell-card')) grid.remove();
    }
  }

  parseList(value) {
    return (value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  hasExcludedTag(card, excludeTags) {
    if (!excludeTags.length) return false;
    const tags = this.parseList(card.dataset.productTags);
    return tags.some((tag) => excludeTags.includes(tag));
  }
}

customElements.define('upsell-recommendations', UpsellRecommendations);
